import React, { useState, useEffect } from "react";
import { Star, Check, ExternalLink, Image as ImageIcon } from "lucide-react";
import { Product } from "../types";

interface ProductImageProps {
  src: string;
  alt: string;
  className?: string;
}

export const ProductImage: React.FC<ProductImageProps> = ({
  src,
  alt,
  className,
}) => {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [hasError, setHasError] = useState(false);

  // Reset state when the prop changes
  useEffect(() => {
    setCurrentSrc(src);
    setHasError(false);
  }, [src]);

  const handleError = () => {
    console.warn(`[Image Load Failed] Could not load: ${currentSrc}`);

    // If it's an external URL (http), just fail immediately
    if (currentSrc.startsWith("http")) {
      setHasError(true);
      return;
    }

    // Fallback logic for local files: try jpg -> png -> jpeg -> fail
    if (currentSrc.endsWith(".jpg")) {
      console.log(`[Image Fallback] Trying .png for ${currentSrc}`);
      setCurrentSrc(currentSrc.replace(".jpg", ".png"));
    } else if (currentSrc.endsWith(".png")) {
      console.log(`[Image Fallback] Trying .jpeg for ${currentSrc}`);
      setCurrentSrc(currentSrc.replace(".png", ".jpeg"));
    } else {
      // If we've tried alternatives and failed, show placeholder
      setHasError(true);
    }
  };

  if (hasError || !src) {
    return (
      <div
        className={`h-full w-full bg-slate-50 flex flex-col items-center justify-center text-slate-300 p-4 text-center ${className}`}
      >
        <ImageIcon size={48} strokeWidth={1} className="mb-2" />
        <span className="text-[10px] uppercase font-bold tracking-widest text-slate-400 line-clamp-2">
          {alt}
        </span>
      </div>
    );
  }

  return (
    <img
      src={currentSrc}
      alt={alt}
      className={
        className || "max-h-full max-w-full object-contain mix-blend-multiply"
      }
      onError={handleError}
    />
  );
};

interface ProductCardProps {
  product: Product;
  trackEvent: (name: string, params?: Record<string, any>) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, trackEvent }) => {
  const handleClick = () => {
    // Standardized event name for better analytics aggregation
    trackEvent("product_click", { brand: product.brand, name: product.name });
    trackEvent("amazon_redirect", { url: product.link });
    window.open(product.link, "_blank");
  };

  return (
    <div
      className={`group relative bg-white border ${
        product.isHero
          ? "border-orange-500 ring-1 ring-orange-500"
          : "border-slate-200"
      } flex flex-col h-full hover:shadow-xl transition-shadow duration-300 rounded-xl overflow-hidden`}
    >
      {product.isHero && (
        <div className="bg-orange-600 text-white text-[11px] font-bold py-1.5 text-center uppercase tracking-widest z-10 relative">
          Top Recommendation
        </div>
      )}

      {/* Product Image Area */}
      <div className="h-56 w-full bg-white flex items-center justify-center p-6 border-b border-slate-50 relative">
        <ProductImage
          src={product.image}
          alt={`${product.brand} ${product.name}`}
        />

        {/* Subtle grid pattern background for lab feel */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(#f1f5f9 1px, transparent 1px)",
            backgroundSize: "10px 10px",
            zIndex: 0,
          }}
        ></div>
      </div>

      <div className="p-6 flex-1 flex flex-col">
        {/* Brand & Name */}
        <div className="mb-4">
          <div className="flex justify-between items-start mb-1">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              {product.brand}
            </p>
            {product.isHero && (
              <Star size={14} className="text-orange-500 fill-orange-500" />
            )}
          </div>
          <h3 className="text-xl font-bold text-slate-900 leading-tight">
            {product.name}
          </h3>
        </div>

        {/* Best For Badge */}
        {product.bestFor && (
          <div className="inline-block bg-slate-100 text-slate-700 text-xs font-semibold px-3 py-1 rounded-full mb-6 self-start">
            Best for: {product.bestFor}
          </div>
        )}

        {/* Price & Size */}
        <div className="flex items-center gap-3 mb-6 pb-6 border-b border-slate-100">
          <span className="text-3xl font-bold text-slate-900">
            {product.price}
          </span>
          <span className="text-xs text-slate-500 font-medium px-2 py-1 bg-slate-50 rounded-md border border-slate-100">
            {product.size}
          </span>
        </div>

        {/* Features Checklist */}
        <div className="flex-1">
          {product.features && product.features.length > 0 && (
            <ul className="space-y-3 mb-6">
              {product.features.map((feat, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-sm text-slate-700"
                >
                  <div className="w-5 h-5 rounded-full bg-green-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Check
                      size={12}
                      className="text-green-600"
                      strokeWidth={3}
                    />
                  </div>
                  {feat}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* UGC / Review */}
        {product.ugc && (
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 relative mt-auto">
            <div className="flex gap-0.5 mb-2">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={10}
                  className="text-yellow-400 fill-yellow-400"
                />
              ))}
            </div>
            <p className="text-xs italic text-slate-600 leading-relaxed">
              "{product.ugc}"
            </p>
            <p className="text-[10px] text-slate-400 mt-2 font-bold">
              — {product.ugcAuthor}
            </p>
          </div>
        )}
      </div>

      {/* CTA Button */}
      <div className="p-4 bg-white border-t border-slate-100">
        <button
          onClick={handleClick}
          className={`w-full py-3.5 px-4 rounded-lg text-sm font-bold uppercase tracking-wide flex items-center justify-center gap-2 transition-all shadow-sm ${
            product.isHero
              ? "bg-orange-600 text-white hover:bg-orange-700 hover:shadow-md"
              : "bg-slate-900 text-white hover:bg-slate-800 hover:shadow-md"
          }`}
        >
          Buy on Amazon <ExternalLink size={14} />
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
